interface LinearConfigurationDTOCommon {
  configId: string;
  designation: string | null;
  description: string | null;
  schaefflerId: string | null;

  kbInfo: string[] | undefined;

  kmats: KmatDTO[];
  steps: LinearStepDTO[];

  cadUrl: string;
  cadXmlRequest: string;

  manufacturerLogo: string | null;
  baseExternalUrl: string;
  mediaUrl: string;
}

export interface LinearConfigurationDTO extends LinearConfigurationDTOCommon {
  productCode: string;
  systemLayoutHelpTile: TileDTO | null;
  length: number | null;
  minLength: number | null;
  maxLength: number | null;
  allowedSteps: boolean[][];
  complete: boolean | null;
  step5PdfLink: string | null;
  currentStep: number | null;
  highestVisitedStep: number | null;
  guidewayPieceLimits: Dict<{ minLength: number; maxLength: number }>;
  customSegmentLengthSelected: boolean | null;
  specialTranslations: Dict<string> | null;
  resetCarriageDetails: boolean | null;
  configVersion: string | null;
  customSectionHeightSelected: boolean | null;
  defaultPlugsApplied: boolean | null;
  enableConfigureCarriageValues: boolean | null;
  enableCarriageEdit: boolean[] | undefined;
  priceReference: string;
}

export interface KmatDTO {
  id: string;
  parentId?: string;
  cstics: LinearCsticDTO[];
}

export interface LinearConfigurationUpdateDTO extends LinearConfigurationDTOCommon {
  productCode: string;
  conflictedCstics: ConflictKmatDTO[] | null;

  length: number | null;
  minLength: number | null;
  maxLength: number | null;
  currentStep: number | null;
  highestVisitedStep: number | null;
  guidewayPieceLimits: Dict<{ minLength: number; maxLength: number }>;
  complete: boolean | null;
  largeDesignSelected: boolean | null;
  customSegmentLengthSelected: boolean | null;
  specialTranslations: Dict<string> | null;
  resetCarriageDetails: boolean | null;
  configVersion: string | null;
  enableConfigureCarriageValues: boolean | null;
  priceReference: string;
  enableCarriageEdit: boolean[] | undefined;
}

export interface LinearStepDTO {
  name: string;
  active: boolean;
  substeps: SubstepDTO[] | null;
}

export enum SubstepType {
  SystemLayout = "SystemLayout",
  DesignType = "DesignType",
  Size = "Size",
  Preload = "Preload",
  Accuracy = "Accuracy",
  HeightSorting = "HeightSorting",
  LocatingFace = "LocatingFace",
  Length = "Length",
  SystemCoating = "SystemCoating",
  Lubrication = "Lubrication",
  Attachment = "Attachment",
  Closure = "Closure",
  GuidewaySet = "GuidewaySet",
  ReferenceCarriage = "ReferenceCarriage",
  SMDS = "SMDS",
  SMDE = "SMDE",
  CarriageDetails = "CarriageDetails",
  SummaryPage = "SummaryPage",

  // Carriage specific
  Series = "Series",
  CarriageType = "CarriageType",
  ConfigureCarriage = "ConfigureCarriage",

  // Rail specific
  Mounting = "Mounting",
  ConfigureRail = "ConfigureRail",

  // Shaft specific
  Diameter = "Diameter",
  Material = "Material",
  Coating = "Coating",
  Tolerance = "Tolerance",
  ShaftLength = "ShaftLength",
  ShaftTolerance = "ToleranceShaftLength",
  ShaftMachining = "ShaftMachining",
  ShaftMachiningExtra = "ShaftMachiningExtra",
}

export enum SubstepFooterInfoType {
  Message,
  Text,
}

export interface SubstepDTO {
  type: SubstepType;
  name: string;
  description: string;
  info: string;
  tiles: ElementTileDTO[] | null;
  groupedTiles: TileGroupDTO[] | null;
  tilesPerRow: number | null;
  defaultValues: { [csticName: string]: string } | null; // Values to set when resetting the entire step
  dynamicSubstep?: boolean; // Determine whether to update tile information - defaultValues, tiles, groupedTiles, tilesPerRow,
  visible: boolean;
  footerInfo?: string | string[] | null;
}

export interface TileGroupDTO {
  name: string;
  description: string;
  info: string;
  tiles: ElementTileDTO[] | null;
}

export interface ElementTileDTO {
  name: string;
  nameIsExternalTranslation: boolean | null;
  description: string;
  info: string;
  imageUrl: string;
  cstics: { [csticName: string]: { value: string } | { clear: boolean } }; // Map of cstics to set when selecting this tile
  disabled?: boolean;
}

export interface LinearCsticDTO {
  key: string;
  name: string;
  label: string;
  values: LinearCsticValueDTO[];
  type: string;
  info: string;
  description: string;
  value: string;
  readonly: boolean;
}
export interface LinearCsticValueDTO {
  key: string;
  label: string;
  value: string;
  info: string;
  imageUrl: string;
  standard?: boolean;
  defaultValue: boolean;
}
export interface TileDTO {
  label: string;
  imageUrl: string;
  linkUrl: string;
}

export interface ConflictKmatDTO {
  id: string;
  name: string;
  description: string;
  cstics: ConflictCsticDTO[];
}

export type ConflictCsticDTO = Pick<LinearCsticDTO, "key" | "name" | "value"> & { lastValidValue: string };

export interface SavedConfigurationDTO {
  code: string;
  designation: string;
  mediaUrl: string;
  name: string | null;
  schaefflerId: string | null;
  manufacturerUrl: string | null;
  shortDescription: string | null;
  comment: string | null;
  showComment: boolean | null;
  importUrlPrefix: string | null;
  baseExternalUrl: string;
}
